import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Pipe, Component, ChangeDetectionStrategy, Optional, Inject, Input, ViewChild, NgModule, makeEnvironmentProviders } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';
import * as i1 from '@angular/platform-browser';
import { NgIf, NgFor, CommonModule } from '@angular/common';
const _c0 = ["overlay"];
const _c1 = ["*"];
function NgxSpinnerComponent_div_0_div_2_div_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div");
  }
}
function NgxSpinnerComponent_div_0_div_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtemplate(1, NgxSpinnerComponent_div_0_div_2_div_1_Template, 1, 0, "div", 6);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵclassMap(ctx_r0.spinner.class);
    i0.ɵɵstyleProp("color", ctx_r0.spinner.color);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngForOf", ctx_r0.spinner.divArray);
  }
}
function NgxSpinnerComponent_div_0_div_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 7);
    i0.ɵɵpipe(1, "safeHtml");
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("innerHTML", i0.ɵɵpipeBind1(1, 1, ctx_r0.template), i0.ɵɵsanitizeHtml);
  }
}
function NgxSpinnerComponent_div_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2, 0);
    i0.ɵɵtemplate(2, NgxSpinnerComponent_div_0_div_2_Template, 2, 5, "div", 3)(3, NgxSpinnerComponent_div_0_div_3_Template, 2, 3, "div", 4);
    i0.ɵɵelementStart(4, "div", 5);
    i0.ɵɵprojection(5);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵstyleProp("background-color", ctx_r0.spinner.bdColor)("z-index", ctx_r0.spinner.zIndex)("position", ctx_r0.spinner.fullScreen ? "fixed" : "absolute");
    i0.ɵɵproperty("@.disabled", ctx_r0.disableAnimation)("@fadeIn", "in");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", !ctx_r0.template);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r0.template);
    i0.ɵɵadvance();
    i0.ɵɵstyleProp("z-index", ctx_r0.spinner.zIndex);
  }
}
const LOADERS = {
  "ball-8bits": 16,
  "ball-atom": 4,
  "ball-beat": 3,
  "ball-circus": 5,
  "ball-climbing-dot": 4,
  "ball-clip-rotate": 1,
  "ball-clip-rotate-multiple": 2,
  "ball-clip-rotate-pulse": 2,
  "ball-elastic-dots": 5,
  "ball-fall": 3,
  "ball-fussion": 4,
  "ball-grid-beat": 9,
  "ball-grid-pulse": 9,
  "ball-newton-cradle": 4,
  "ball-pulse": 3,
  "ball-pulse-rise": 5,
  "ball-pulse-sync": 3,
  "ball-rotate": 1,
  "ball-running-dots": 5,
  "ball-scale": 1,
  "ball-scale-multiple": 3,
  "ball-scale-pulse": 2,
  "ball-scale-ripple": 1,
  "ball-scale-ripple-multiple": 3,
  "ball-spin": 8,
  "ball-spin-clockwise": 8,
  "ball-spin-clockwise-fade": 8,
  "ball-spin-clockwise-fade-rotating": 8,
  "ball-spin-fade": 8,
  "ball-spin-fade-rotating": 8,
  "ball-spin-rotate": 2,
  "ball-square-clockwise-spin": 8,
  "ball-square-spin": 8,
  "ball-triangle-path": 3,
  "ball-zig-zag": 2,
  "ball-zig-zag-deflect": 2,
  cog: 1,
  "cube-transition": 2,
  fire: 3,
  "line-scale": 5,
  "line-scale-party": 5,
  "line-scale-pulse-out": 5,
  "line-scale-pulse-out-rapid": 5,
  "line-spin-clockwise-fade": 8,
  "line-spin-clockwise-fade-rotating": 8,
  "line-spin-fade": 8,
  "line-spin-fade-rotating": 8,
  pacman: 6,
  "square-jelly-box": 2,
  "square-loader": 1,
  "square-spin": 1,
  timer: 1,
  "triangle-skew-spin": 1
};
const DEFAULTS = {
  BD_COLOR: "rgba(51,51,51,0.8)",
  SPINNER_COLOR: "#fff",
  Z_INDEX: 99999
};
const PRIMARY_SPINNER = "primary";
class NgxSpinner {
  constructor(init) {
    Object.assign(this, init);
  }
  static create(init) {
    if (!init?.template && !init?.type) {
      console.warn(`[ngx-spinner]: Property "type" is missed. Please, provide animation type to <ngx-spinner> component
        and ensure css is added to angular.json file`);
    }
    return new NgxSpinner(init);
  }
}
let NgxSpinnerService = /*#__PURE__*/(() => {
  class NgxSpinnerService {
    /**
     * Creates an instance of NgxSpinnerService.
     * @memberof NgxSpinnerService
     */
    constructor() {
      /**
       * Spinner observable
       *
       * @memberof NgxSpinnerService
       */
      // private spinnerObservable = new ReplaySubject<NgxSpinner>(1);
      this.spinnerObservable = new BehaviorSubject(null);
    }
    /**
     * Get subscription of desired spinner
     * @memberof NgxSpinnerService
     **/
    getSpinner(name) {
      return this.spinnerObservable.asObservable().pipe(filter(x => x && x.name === name));
    }
    /**
     * To show spinner
     *
     * @memberof NgxSpinnerService
     */
    show(name = PRIMARY_SPINNER, spinner) {
      return new Promise((resolve, _reject) => {
        setTimeout(() => {
          if (spinner && Object.keys(spinner).length) {
            spinner["name"] = name;
            this.spinnerObservable.next(new NgxSpinner({
              ...spinner,
              show: true
            }));
            resolve(true);
          } else {
            this.spinnerObservable.next(new NgxSpinner({
              name,
              show: true
            }));
            resolve(true);
          }
        }, 10);
      });
    }
    /**
     * To hide spinner
     *
     * @memberof NgxSpinnerService
     */
    hide(name = PRIMARY_SPINNER, debounce = 10) {
      return new Promise((resolve, _reject) => {
        setTimeout(() => {
          this.spinnerObservable.next(new NgxSpinner({
            name,
            show: false
          }));
          resolve(true);
        }, debounce);
      });
    }
    static {
      this.ɵfac = function NgxSpinnerService_Factory(t) {
        return new (t || NgxSpinnerService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: NgxSpinnerService,
        factory: NgxSpinnerService.ɵfac,
        providedIn: "root"
      });
    }
  }
  return NgxSpinnerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const NGX_SPINNER_CONFIG = new InjectionToken("NGX_SPINNER_CONFIG");
let SafeHtmlPipe = /*#__PURE__*/(() => {
  class SafeHtmlPipe {
    constructor(_sanitizer) {
      this._sanitizer = _sanitizer;
    }
    transform(v) {
      if (v) {
        return this._sanitizer.bypassSecurityTrustHtml(v);
      }
    }
    static {
      this.ɵfac = function SafeHtmlPipe_Factory(t) {
        return new (t || SafeHtmlPipe)(i0.ɵɵdirectiveInject(i1.DomSanitizer, 16));
      };
    }
    static {
      this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
        name: "safeHtml",
        type: SafeHtmlPipe,
        pure: true,
        standalone: true
      });
    }
  }
  return SafeHtmlPipe;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgxSpinnerComponent = /*#__PURE__*/(() => {
  class NgxSpinnerComponent {
    // TODO: https://github.com/Napster2210/ngx-spinner/issues/259
    // @HostListener("document:keydown", ["$event"])
    // handleKeyboardEvent(event: KeyboardEvent) {
    //   if (this.spinnerDOM && this.spinnerDOM.nativeElement) {
    //     if (
    //       this.fullScreen ||
    //       (!this.fullScreen && this.isSpinnerZone(event.target))
    //     ) {
    //       event.returnValue = false;
    //       event.preventDefault();
    //     }
    //   }
    // }
    /**
     * Creates an instance of NgxSpinnerComponent.
     *
     * @memberof NgxSpinnerComponent
     */
    constructor(spinnerService, changeDetector, elementRef, globalConfig) {
      this.spinnerService = spinnerService;
      this.changeDetector = changeDetector;
      this.elementRef = elementRef;
      this.globalConfig = globalConfig;
      /**
       * To enable/disable animation
       *
       * @type {boolean}
       * @memberof NgxSpinnerComponent
       */
      this.disableAnimation = false;
      /**
       * Spinner Object
       *
       * @memberof NgxSpinnerComponent
       */
      this.spinner = new NgxSpinner();
      /**
       * Unsubscribe from spinner's observable
       *
       * @memberof NgxSpinnerComponent
       **/
      this.ngUnsubscribe = new Subject();
      /**
       * To set default ngx-spinner options
       *
       * @memberof NgxSpinnerComponent
       */
      this.setDefaultOptions = () => {
        const {
          type
        } = this.globalConfig ?? {};
        this.spinner = NgxSpinner.create({
          name: this.name,
          bdColor: this.bdColor,
          size: this.size,
          color: this.color,
          type: this.type ?? type,
          fullScreen: this.fullScreen,
          divArray: this.divArray,
          divCount: this.divCount,
          show: this.show,
          zIndex: this.zIndex,
          template: this.template,
          showSpinner: this.showSpinner
        });
      };
      this.bdColor = DEFAULTS.BD_COLOR;
      this.zIndex = DEFAULTS.Z_INDEX;
      this.color = DEFAULTS.SPINNER_COLOR;
      this.size = "large";
      this.fullScreen = true;
      this.name = PRIMARY_SPINNER;
      this.template = null;
      this.showSpinner = false;
      this.divArray = [];
      this.divCount = 0;
      this.show = false;
    }
    initObservable() {
      this.spinnerService.getSpinner(this.name).pipe(takeUntil(this.ngUnsubscribe)).subscribe(spinner => {
        this.setDefaultOptions();
        Object.assign(this.spinner, spinner);
        if (spinner.show) {
          this.onInputChange();
        }
        this.changeDetector.detectChanges();
      });
    }
    /**
     * Initialization method
     *
     * @memberof NgxSpinnerComponent
     */
    ngOnInit() {
      this.setDefaultOptions();
      this.initObservable();
    }
    /**
     * To check event triggers inside the Spinner Zone
     *
     * @param {*} element
     * @returns {boolean}
     * @memberof NgxSpinnerComponent
     */
    isSpinnerZone(element) {
      if (element === this.elementRef.nativeElement.parentElement) {
        return true;
      }
      return element.parentNode && this.isSpinnerZone(element.parentNode);
    }
    /**
     * On changes event for input variables
     *
     * @memberof NgxSpinnerComponent
     */
    ngOnChanges(changes) {
      for (const propName in changes) {
        if (propName) {
          const changedProp = changes[propName];
          if (changedProp.isFirstChange()) {
            return;
          } else if (typeof changedProp.currentValue !== "undefined" && changedProp.currentValue !== changedProp.previousValue) {
            if (changedProp.currentValue !== "") {
              this.spinner[propName] = changedProp.currentValue;
              if (propName === "showSpinner") {
                if (changedProp.currentValue) {
                  this.spinnerService.show(this.spinner.name, this.spinner);
                } else {
                  this.spinnerService.hide(this.spinner.name);
                }
              }
              if (propName === "name") {
                this.initObservable();
              }
            }
          }
        }
      }
    }
    /**
     * To get class for spinner
     *
     * @memberof NgxSpinnerComponent
     */
    getClass(type, size) {
      this.spinner.divCount = LOADERS[type];
      this.spinner.divArray = Array(this.spinner.divCount).fill(0).map((_, i) => i);
      let sizeClass = "";
      switch (size.toLowerCase()) {
        case "small":
          sizeClass = "la-sm";
          break;
        case "medium":
          sizeClass = "la-2x";
          break;
        case "large":
          sizeClass = "la-3x";
          break;
        default:
          break;
      }
      return "la-" + type + " " + sizeClass;
    }
    /**
     * Check if input variables have changed
     *
     * @memberof NgxSpinnerComponent
     */
    onInputChange() {
      this.spinner.class = this.getClass(this.spinner.type, this.spinner.size);
    }
    /**
     * Component destroy event
     *
     * @memberof NgxSpinnerComponent
     */
    ngOnDestroy() {
      this.ngUnsubscribe.next();
      this.ngUnsubscribe.complete();
    }
    static {
      this.ɵfac = function NgxSpinnerComponent_Factory(t) {
        return new (t || NgxSpinnerComponent)(i0.ɵɵdirectiveInject(NgxSpinnerService), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(NGX_SPINNER_CONFIG, 8));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NgxSpinnerComponent,
        selectors: [["ngx-spinner"]],
        viewQuery: function NgxSpinnerComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c0, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.spinnerDOM = _t.first);
          }
        },
        inputs: {
          bdColor: "bdColor",
          size: "size",
          color: "color",
          type: "type",
          fullScreen: "fullScreen",
          name: "name",
          zIndex: "zIndex",
          template: "template",
          showSpinner: "showSpinner",
          disableAnimation: "disableAnimation"
        },
        standalone: true,
        features: [i0.ɵɵNgOnChangesFeature, i0.ɵɵStandaloneFeature],
        ngContentSelectors: _c1,
        decls: 1,
        vars: 1,
        consts: [["overlay", ""], ["class", "ngx-spinner-overlay", 3, "background-color", "z-index", "position", 4, "ngIf"], [1, "ngx-spinner-overlay"], [3, "class", "color", 4, "ngIf"], [3, "innerHTML", 4, "ngIf"], [1, "loading-text"], [4, "ngFor", "ngForOf"], [3, "innerHTML"]],
        template: function NgxSpinnerComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵtemplate(0, NgxSpinnerComponent_div_0_Template, 6, 12, "div", 1);
          }
          if (rf & 2) {
            i0.ɵɵproperty("ngIf", ctx.spinner.show);
          }
        },
        dependencies: [SafeHtmlPipe, NgIf, NgFor],
        styles: [".ngx-spinner-overlay[_ngcontent-%COMP%]{position:fixed;top:0;left:0;width:100%;height:100%}.ngx-spinner-overlay[_ngcontent-%COMP%] > div[_ngcontent-%COMP%]:not(.loading-text){top:50%;left:50%;margin:0;position:absolute;transform:translate(-50%,-50%)}.loading-text[_ngcontent-%COMP%]{position:absolute;top:60%;left:50%;transform:translate(-50%,-60%)}"],
        data: {
          animation: [trigger("fadeIn", [state("in", style({
            opacity: 1
          })), transition(":enter", [style({
            opacity: 0
          }), animate(300)]), transition(":leave", animate(200, style({
            opacity: 0
          })))])]
        },
        changeDetection: 0
      });
    }
  }
  return NgxSpinnerComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgxSpinnerModule = /*#__PURE__*/(() => {
  class NgxSpinnerModule {
    static forRoot(config) {
      return {
        ngModule: NgxSpinnerModule,
        providers: [{
          provide: NGX_SPINNER_CONFIG,
          useValue: config
        }]
      };
    }
    static {
      this.ɵfac = function NgxSpinnerModule_Factory(t) {
        return new (t || NgxSpinnerModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NgxSpinnerModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CommonModule]
      });
    }
  }
  return NgxSpinnerModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const provideSpinnerConfig = config => {
  const providers = [{
    provide: NGX_SPINNER_CONFIG,
    useValue: config
  }];
  return makeEnvironmentProviders(providers);
};

/**
 * Generated bundle index. Do not edit.
 */

export { DEFAULTS, LOADERS, NgxSpinner, NgxSpinnerComponent, NgxSpinnerModule, NgxSpinnerService, PRIMARY_SPINNER, provideSpinnerConfig };
